import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../../context/GameContext";
import { useSocket } from "../../context/SocketContext";
import TypewriterText from "../ui/TypewriterText";
import GameInstructions from "../ui/GameInstructions";
import SettingsMenu from "../ui/SettingsMenu";

const HomePage: React.FC = () => {
  const [name, setName] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [error, setError] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const { gameState, createRoom, joinRoom } = useGame();
  const { isConnected } = useSocket();
  const navigate = useNavigate();

  const wittyMessages = [
    "Trust no one, especially your friends...",
    "The night hides many secrets...",
    "The doctor can save lives, but not their own...",
    "The detective sees what others miss...",
    "Innocent until proven guilty? Not in this town...",
    "The mafia strikes when you least expect it...",
    "Your poker face may save your life...",
    "In this game, lying is a virtue...",
    "Sometimes the best defense is a good accusation...",
    "The quietest players often have the most to hide...",
    "Revenge is a dish best served with an alibi...",
    "Everyone's suspicious when death comes knocking...",
    "In Mafia, even your shadow might betray you...",
    "When in doubt, blame the detective...",
    "Death is temporary, but grudges last forever...",
    "The best liars are the ones who mix in truth...",
    "Survival tip: Don't be too helpful, or too useless...",
    "Villagers who speak too much tend to disappear...",
  ];

  const handleCreateRoom = async () => {
    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }

    setError("");
    // Pass the name when creating the room
    createRoom(name);
  };

  const handleJoinRoom = async () => {
    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }

    if (!roomCode.trim()) {
      setError("Please enter a room code");
      return;
    }

    setError("");
    setIsJoining(true);

    try {
      const result = await joinRoom(roomCode, name);
      if (result.success) {
        navigate(`/room/${result.roomId}`);
      } else {
        setError(result.error || "Could not join room. Check your room code.");
      }
    } catch (err) {
      setError("An error occurred while joining the room");
      console.error(err);
    } finally {
      setIsJoining(false);
    }
  };

  // If room created, redirect to room page
  React.useEffect(() => {
    if (gameState.roomId) {
      navigate(`/room/${gameState.roomId}`);
    }
  }, [gameState.roomId, navigate]);

  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === "development";

  return (
    <div className="min-h-screen homepage-bg flex flex-col items-center justify-center p-4 relative">
      {/* Settings menu in top right */}
      <div className="absolute top-4 right-4 z-20">
        <SettingsMenu showDebugOption={isDevelopment} />
      </div>

      {/* Main content */}
      <div className="w-full max-w-md z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <h1 className="text-5xl font-bold text-indigo-400 animate-float drop-shadow-lg">
              MAFIA
            </h1>
          </div>

          <div className="mt-4 mb-6">
            <TypewriterText
              messages={wittyMessages}
              typingSpeed={40}
              delayBetweenMessages={5000}
            />
          </div>

          <div className="flex justify-center space-x-6 mt-3 mb-4">
            <div
              className="text-center animate-float"
              style={{ animationDelay: "0.2s" }}
            >
              <div className="w-12 h-12 bg-red-900/80 rounded-full flex items-center justify-center mx-auto mb-1">
                <span className="text-xl">🕵️</span>
              </div>
              <p className="text-xs text-red-400">Mafia</p>
            </div>
            <div
              className="text-center animate-float"
              style={{ animationDelay: "0.4s" }}
            >
              <div className="w-12 h-12 bg-green-900/80 rounded-full flex items-center justify-center mx-auto mb-1">
                <span className="text-xl">💉</span>
              </div>
              <p className="text-xs text-green-400">Doctor</p>
            </div>
            <div
              className="text-center animate-float"
              style={{ animationDelay: "0.6s" }}
            >
              <div className="w-12 h-12 bg-blue-900/80 rounded-full flex items-center justify-center mx-auto mb-1">
                <span className="text-xl">🔍</span>
              </div>
              <p className="text-xs text-blue-400">Detective</p>
            </div>
            <div
              className="text-center animate-float"
              style={{ animationDelay: "0.8s" }}
            >
              <div className="w-12 h-12 bg-yellow-900/80 rounded-full flex items-center justify-center mx-auto mb-1">
                <span className="text-xl">👨‍🌾</span>
              </div>
              <p className="text-xs text-yellow-400">Villager</p>
            </div>
          </div>

          {!isConnected && (
            <div className="text-red-500 text-sm mt-2">
              Connecting to server...
            </div>
          )}
        </div>

        <div className="card-transparent backdrop-blur-sm bg-slate-800/30 shadow-2xl">
          <div className="mb-5">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-slate-300 mb-1"
            >
              Your Name
            </label>
            <input
              type="text"
              id="name"
              className="input w-full"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isConnected}
            />
          </div>

          <div className="space-y-4 mt-6">
            <button
              onClick={handleCreateRoom}
              className="btn btn-primary w-full"
              disabled={!isConnected}
            >
              Create New Room
            </button>

            <div className="flex items-center">
              <div className="flex-grow h-px bg-slate-700"></div>
              <div className="px-4 text-sm text-slate-500">OR</div>
              <div className="flex-grow h-px bg-slate-700"></div>
            </div>

            <div className="mb-4">
              <label
                htmlFor="room-code"
                className="block text-sm font-medium text-slate-300 mb-1"
              >
                Room Code
              </label>
              <input
                type="text"
                id="room-code"
                className="input w-full"
                placeholder="Enter room code"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                disabled={!isConnected}
              />
            </div>

            <button
              onClick={handleJoinRoom}
              className="btn btn-secondary w-full"
              disabled={!isConnected || isJoining}
            >
              {isJoining ? "Joining..." : "Join Room"}
            </button>
          </div>

          {error && (
            <div className="mt-4 text-red-500 text-sm text-center">{error}</div>
          )}
        </div>

        {/* Game Instructions */}
        <GameInstructions />

        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Created for entertainment purposes. No actual mafias were harmed in
            the making of this game.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
